/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import logo from '../../../assets/images/logo.png';
import searchIcon from '../../../assets/images/search-icon.png';
import { updateCadence } from '../../actions/cadenceActions';
import { openModal } from '../../actions/modalActions';
import { logout } from '../../actions/userActions';
import { tourInactive } from '../../actions/tourActions';
import {
  updateWorkPackageSearchText,
  resetWorkPackageSearchText
} from '../../actions/defaultOptionsActions';
import modalTypes from '../../constants/modalTypes';
import CriteriaModal from '../Criteria/criteriaModal';
import GlobalSpinner from '../Common/globalSpinner';
import Settings from './settings';
import New from './new';
import Listwall from './listwall';
import Cardwall from './cardwall';
import Manage from './manage';
import Plan from './plan';
import Kanban from './kanban';
import Trash from './trash';
import { isKanbanMode, hasScrumTeam } from '../../selectors/teamSelector';
import SpacesMenu from '../Common/spacesMenu';
import {
  currentOrganization,
  generateOrgLevelDefaults
} from '../../utils/organizationHelpers';
import { allowsUser } from '../../utils/authorization';
import { CadenceStatuses } from '../../constants/storyStatusTypes';
import GuideMe from '../Tour/GuideMe';
import ErrorBoundaryWrapper from '../ErrorBoundaries/ErrorBoundaryWrapper';

class Header extends Component {
  constructor(props) {
    super(props);
    this.searchInput = React.createRef();
    this.state = {
      editCriteriaOpen: false,
      isInputVisible: false
    };
  }

  componentDidUpdate() {
    if (!this.isPlanWallWithFirstFourLevels() && this.props.searchText)
      this.props.resetWorkPackageSearchText();
  }

  addItem = (level, organizationLevelId) => {
    if (level === 2) {
      this.props.openModal(
        modalTypes.newFeature,
        { organizationLevelId },
        'newFeature'
      );
    } else if (level === 3) {
      this.props.openModal(
        modalTypes.newCapability,
        { organizationLevelId },
        'newCapability'
      );
    } else if (level === 4) {
      this.props.openModal(
        modalTypes.newEpic,
        { organizationLevelId },
        'newEpic'
      );
    } else {
      this.props.openModal(
        modalTypes.newResult,
        { organizationLevelId },
        'newResult'
      );
    }
  };

  openEditCriteria = (type) => {
    this.setState({ editCriteriaOpen: true, type });
  };

  closeEditCriteria = (type) => {
    this.setState({ editCriteriaOpen: false, type });
  };

  signOut = () => {
    this.props.tourInactive();
    this.props.logout().then(() => {
      window.App.disconnect();
    });
  };

  isPlanWallWithFirstFourLevels = () => {
    const {
      location: { pathname }
    } = this.props;
    const paths = pathname.split('/');
    if ((paths[1] === 'planwall' && paths[3] < 5) || paths[1] === 'cardwall')
      return true;
    return false;
  };

  showInputField = () => this.setState({ isInputVisible: true });

  hideInputField = ({ target }) => {
    if (!target.value) this.setState({ isInputVisible: false });
  };

  clearSearchField = () => {
    this.props.resetWorkPackageSearchText();
    this.searchInput.current.focus();
  };

  renderSearchField = () => {
    const { searchText } = this.props;
    const { isInputVisible } = this.state;
    if (this.isPlanWallWithFirstFourLevels())
      return (
        <div className={`search-container${!isInputVisible ? '-hide' : ''}`}>
          {isInputVisible && (
            <div className="search-field">
              <input
                value={searchText}
                autoFocus={isInputVisible}
                ref={this.searchInput}
                onBlur={this.hideInputField}
                onChange={(e) =>
                  this.props.updateWorkPackageSearchText(e.target.value)}
              />
            </div>
          )}
          <div className="search-icon">
            {searchText ? (
              <span
                className="search-close-btn"
                onClick={this.clearSearchField}
              >
                &times;
              </span>
            ) : (
              <div onClick={this.showInputField}>
                <img src={searchIcon} alt="search-icon" />
              </div>
            )}
          </div>
        </div>
      );
    return null;
  };

  render() {
    if (loginPage) return null;

    const {
      currentTeams,
      currentProducts,
      isKanban,
      hasScrum,
      sprints,
      organizationLevels
    } = this.props;
    const loginPage = window.location.hash.includes('login');
    const currentSprint = isKanban
      ? null
      : sprints.find((item) => item.status === CadenceStatuses.IN_PROGRESS);
    const planned = isKanban
      ? null
      : sprints.find(
          (item) =>
            item.status === CadenceStatuses.GETTING_READY && !item.deleted_at
        );
    const allowsAdmin = allowsUser('administer');

    return (
      this.props.signedIn && (
        <>
          <div id="header-container" className="header-container">
            <div className="logo">
              <Link to="/planwall/r/1">
                <img src={logo} alt="Agile Design logo" />
              </Link>
            </div>

            <div className="header-spinner">
              <GlobalSpinner />
            </div>
            <div className="header">
              <SpacesMenu />
              <GuideMe />
              {this.renderSearchField()}
              <div className="right">
                <New addItem={this.addItem} />
                <Listwall
                  orgLevels={generateOrgLevelDefaults(organizationLevels)}
                />
                {!isKanban && (
                  <Cardwall
                    sprints={this.props.sprints}
                    isKanban={isKanban}
                    multipleTeams={currentTeams.length > 1}
                  />
                )}
                {isKanban && <Kanban />}
                <Plan hasScrum={hasScrum} currentProducts={currentProducts} />
                {allowsAdmin && (
                  <Manage
                    sprints={this.props.sprints}
                    openEditCriteria={this.openEditCriteria}
                  />
                )}
                <Trash />
                <Settings
                  openModal={this.props.openModal}
                  signOut={this.signOut}
                />
              </div>
            </div>
          </div>
          {this.state.editCriteriaOpen ? (
            <CriteriaModal
              type={this.state.type}
              isOpen={this.state.editCriteriaOpen}
              onClose={this.closeEditCriteria}
            />
          ) : null}
        </>
      )
    );
  }
}

Header.propTypes = {
  signedIn: PropTypes.bool,
  sprints: PropTypes.array,
  openModal: PropTypes.func,
  logout: PropTypes.func,
  updateCadence: PropTypes.func
};

const mapStateToProps = (state) => {
  const organizationLevels = currentOrganization()?.organization_levels || [];

  return {
    searchText: state.defaultOptionsReducer.workPackageSearchText,
    signedIn: state.userReducer.isLoggedIn,
    sprints: state.cadenceReducer.cadences.filter(
      (cadence) => !cadence.deleted_at
    ),
    workPackages: state.workPackageReducer.all,
    stories: state.workPackageReducer.stories,
    currentTeams: state.defaultOptionsReducer.currentTeams.filter(
      (e) => e.id !== 'unassigned'
    ),
    currentProducts: state.defaultOptionsReducer.currentProducts,
    isKanban: isKanbanMode(state),
    hasScrum: hasScrumTeam(state),
    organizationLevels
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logout,
      tourInactive,
      updateCadence,
      openModal,
      updateWorkPackageSearchText,
      resetWorkPackageSearchText
    },
    dispatch
  );

export default ErrorBoundaryWrapper(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(Header))
);
